import { API } from "@apis/index";
import ToastConstant from "@configs/react-toastify/snackbar";
import { COOKIES, ERROR_MESSAGE, LOCAL_STORAGE, LOGGED_IN_STATUS, TOAST } from "@constants/index";
import { PermissionContext } from "@contexts/PermissionContext";
import { useClientConfig } from "@contexts/ClientConfigContext";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useTheme } from "@hooks/useTheme";
import { createSessionCookie } from "@utils/common";
import { setLogoutInProgress } from "@auth/keycloakClient";
import { isAuthenticated } from "@utils/auth";
import { resetCachedUserData } from "@utils/authSession";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

const LegacyLogin = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { updatePermissions, updateLocaleDefault } = useContext(PermissionContext);
  const clientConfigContext = useClientConfig();
  const fetchClientConfig = clientConfigContext?.fetchClientConfig ?? (async () => {});

  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const isTokenExpired = new URLSearchParams(location.search).get("expired") === "true";

  useEffect(() => {
    setLogoutInProgress(false);
  }, []);

  const getRedirectPathAfterLogin = () => {
    const saved = localStorage.getItem(LOCAL_STORAGE.REDIRECT_AFTER_LOGIN);
    if (saved && !saved.includes("/login")) {
      return saved;
    }
    const from = location.state?.from;
    const fromPath = from
      ? `${from.pathname || ""}${from.search || ""}`
      : "";
    if (fromPath && !fromPath.includes("/login")) {
      return fromPath;
    }
    return "/dashboard";
  };

  useEffect(() => {
    if (isAuthenticated()) {
      navigate(getRedirectPathAfterLogin(), { replace: true });
    }
  }, [navigate, location.state]);

  const resolveLoginErrorMessage = (error) => {
    const body = error?.response?.data;
    const errorMessage = typeof body === "string" ? body : body?.message;
    if (errorMessage === ERROR_MESSAGE.PASSWORD_NOT_CORRECT) {
      return { titleKey: "toast.login.password-not-correct.title", messageKey: "toast.login.password-not-correct.message" };
    }
    if (errorMessage === ERROR_MESSAGE.USER_HAD_EXPIRED) {
      return { titleKey: "toast.login.user-had-expired.title", messageKey: "toast.login.user-had-expired.message" };
    }
    if (errorMessage === ERROR_MESSAGE.USER_HAD_DISABLED) {
      return { titleKey: "toast.login.user-had-disabled.title", messageKey: "toast.login.user-had-disabled.message" };
    }
    if (errorMessage === ERROR_MESSAGE.USER_NOT_FOUND) {
      return { titleKey: "toast.login.user-not-found.title", messageKey: "toast.login.user-not-found.message" };
    }
    if (
      errorMessage === ERROR_MESSAGE.THE_ID_OR_PASSWORD_IS_INCORRECT ||
      errorMessage?.message === ERROR_MESSAGE.THE_ID_OR_PASSWORD_IS_INCORRECT
    ) {
      return { titleKey: "toast.login.error.title", messageKey: "toast.login.error.message-incorrect" };
    }
    if (errorMessage === ERROR_MESSAGE.ACCOUNT_TEMPORARILY_LOCKED) {
      return { titleKey: "toast.login.error.title", literalMessage: t("toast.login.error.account-locked") };
    }
    if (errorMessage === ERROR_MESSAGE.TOO_MANY_REQUESTS_FROM_IP) {
      return { titleKey: "toast.login.error.title", literalMessage: t("toast.login.error.ip-blocked") };
    }
    const literal =
      typeof errorMessage === "string"
        ? errorMessage
        : errorMessage?.message || error?.message || t("toast.login.error.message");
    return { titleKey: "toast.login.error.title", literalMessage: literal };
  };

  const clearFieldError = (field) => {
    setValidationErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }
      return { ...prev, [field]: "" };
    });
  };

  const getInputClassName = (hasError) =>
    `w-full px-3 py-2 border ${hasError ? colors.borderDangerOutLineButton : colors.borderHoverPrimary} ${colors.borderPrimary} rounded-md shadow-sm ${colors.textPlaceholder} ${colors.textSubTitle} focus:outline-none sm:text-sm transition-colors duration-200`;

  const getFormValues = (form) => {
    const formData = new FormData(form);
    return {
      userId: String(formData.get("userId") ?? "").trim(),
      password: String(formData.get("password") ?? ""),
    };
  };

  const validateFormValues = (values) => {
    const errors = {};
    if (!values.userId) {
      errors.userId = "system.user-id-required";
    }
    if (!values.password) {
      errors.password = "system.password-required";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const values = getFormValues(event.currentTarget);
    setUserId(values.userId);
    setPassword(values.password);

    if (!validateFormValues(values)) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await API.post(
        "/login",
        { userId: values.userId, password: values.password },
        { skipErrorToast: true, skipAuthRefresh: true, skipAuth: true },
      );
      const payload = response?.data?.data;
      if (typeof payload === "string" || !payload) {
        throw new Error(t("toast.login.error.message"));
      }

      // Browser close without logout leaves SYNCED_ALL_DATA + IndexedDB from the
      // previous user; clear them so MainLayout syncs fresh data for this login.
      await resetCachedUserData();

      const jwt = payload.jwt;
      localStorage.setItem(LOCAL_STORAGE.SESSION_ID, payload.sessionId ?? "");
      localStorage.setItem(LOCAL_STORAGE.TOKEN, jwt);
      localStorage.setItem(LOCAL_STORAGE.USER_ID, values.userId);
      createSessionCookie(COOKIES.LOGIN_STATUS, LOGGED_IN_STATUS);

      const userPermissions = payload.permissions || [];
      const localeDefault = payload.localeDefault || "ko";
      const existingLanguage = localStorage.getItem(LOCAL_STORAGE.LANGUAGE);
      if (!existingLanguage) {
        localStorage.setItem(LOCAL_STORAGE.LANGUAGE, localeDefault);
      }

      updatePermissions(userPermissions);
      updateLocaleDefault(localeDefault);
      await fetchClientConfig();

      // Dashboard shows announcement popup only when this flag is set after login
      localStorage.setItem(LOCAL_STORAGE.JUST_LOGGED_IN, "true");

      ToastConstant(TOAST.success, t("toast.login.success.title"), t("toast.login.success.message"));

      const redirectTo = getRedirectPathAfterLogin();
      localStorage.removeItem(LOCAL_STORAGE.REDIRECT_AFTER_LOGIN);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      console.error("Error during login:", error);
      const mapped = resolveLoginErrorMessage(error);
      const title = t(mapped.titleKey);
      const message = mapped.literalMessage !== undefined ? mapped.literalMessage : t(mapped.messageKey);
      ToastConstant(TOAST.error, title, message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="space-y-3">
      <div className="text-center">
        <div className={`mx-auto h-12 w-12 flex items-center justify-center rounded-full ${colors.bgIconAccent}`}>
          <svg
            className={`h-8 w-8 ${colors.textActive}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>
      </div>

      {/* Token Expiration Notice */}
      {isTokenExpired && (
        <div className="rounded-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className={`h-5 w-5 ${colors.textActive}`}
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className={`text-sm ${colors.textActive} mt-1`}>
                {t("login.session-expired.message")}
              </p>
            </div>
          </div>
        </div>
      )}

      <form data-testid="login-form" className="space-y-4" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="userId" className={`block text-sm font-medium ${colors.textSubTitle} mb-1`}>
            {t("common.user-id")}
          </label>
          <input
            id="userId"
            name="userId"
            type="text"
            autoComplete="username"
            aria-invalid={!!validationErrors.userId}
            aria-describedby={validationErrors.userId ? "userId-error" : undefined}
            className={getInputClassName(!!validationErrors.userId)}
            placeholder={t("common.user-id")}
            value={userId}
            onChange={(e) => {
              setUserId(e.currentTarget.value);
              clearFieldError("userId");
            }}
          />
          {validationErrors.userId && (
            <p id="userId-error" className={`${colors.textDanger} text-xs mt-1`} data-testid="login-user-id-error">
              {t(validationErrors.userId)}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="password" className={`block text-sm font-medium ${colors.textSubTitle} mb-1`}>
            {t("common.password")}
          </label>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              aria-invalid={!!validationErrors.password}
              aria-describedby={validationErrors.password ? "password-error" : undefined}
              className={`${getInputClassName(!!validationErrors.password)} pr-10`}
              placeholder={t("common.password")}
              value={password}
              onChange={(e) => {
                setPassword(e.currentTarget.value);
                clearFieldError("password");
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeSlashIcon
                  className={`h-5 w-5 ${colors.textSubTitle} ${colors.textHoverActive} transition-colors duration-200`}
                />
              ) : (
                <EyeIcon
                  className={`h-5 w-5 ${colors.textSubTitle} ${colors.textHoverActive} transition-colors duration-200`}
                />
              )}
            </button>
          </div>
          {validationErrors.password && (
            <p id="password-error" className={`${colors.textDanger} text-xs mt-1`} data-testid="login-password-error">
              {t(validationErrors.password)}
            </p>
          )}
        </div>

        <div>
          <button
            type="submit"
            data-testid="login-sign-in-button"
            disabled={isLoading}
            className={`w-full flex justify-center py-2 px-4 rounded-md shadow-sm text-sm font-medium ${colors.textButton} ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200`}
          >
            {isLoading ? (
              <div className="flex items-center">
                <svg
                  className={`animate-spin -ml-1 mr-3 h-5 w-5 ${colors.textButton}`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {t("common.signing-in")}
              </div>
            ) : (
              t("common.sign-in")
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LegacyLogin;
